import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  InputAdornment,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import Inventory2RoundedIcon from '@mui/icons-material/Inventory2Rounded';
import CollectionsBookmarkRoundedIcon from '@mui/icons-material/CollectionsBookmarkRounded';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import VisibilityOffRoundedIcon from '@mui/icons-material/VisibilityOffRounded';

const selectionKey = (selection) => selection?.type === 'catalogue'
  ? `catalogue:${selection?.source || 'promoworld'}:${selection?.catalogueKey || selection?.supplierId || selection?.supplierName || ''}`
  : `product:${selection?.source || 'promoworld'}:${selection?.productId || ''}`;

const productName = (product) => product?.Name || product?.name || product?.Title || `Product ${product?.id || ''}`;
const productSupplier = (product) => product?.Supplier?.Name || product?.SupplierName || product?.supplierName || 'PromoWorld';
const productSupplierId = (product) => String(product?.Supplier?.Id || product?.SupplierId || product?.supplierId || '').trim();
const productNumber = (product) => product?.Number || product?.number || product?.ExternalProductId || product?.id || '';
const productImage = (product) => product?.ImageUrl || product?.imageUrl || (Array.isArray(product?.Images) ? product.Images[0] : '') || '';

export default function PromoShowcaseProductPicker({
  open,
  onClose,
  server,
  headers,
  instanceUniqueId,
  currentSelections = [],
  onSaved,
  cataloguesOnly = false,
}) {
  const [tab, setTab] = useState(cataloguesOnly ? 1 : 0);
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [products, setProducts] = useState([]);
  const [catalogues, setCatalogues] = useState([]);
  const [selectedProductIds, setSelectedProductIds] = useState([]);
  const [error, setError] = useState('');
  const [managingCatalogue, setManagingCatalogue] = useState(null);
  const [catalogueProducts, setCatalogueProducts] = useState([]);
  const [catalogueQuery, setCatalogueQuery] = useState('');
  const [catalogueLoading, setCatalogueLoading] = useState(false);
  const [cataloguePage, setCataloguePage] = useState(1);
  const [catalogueTotalPages, setCatalogueTotalPages] = useState(1);
  const [catalogueTotal, setCatalogueTotal] = useState(0);

  const existingKeys = useMemo(() => new Set((currentSelections || []).map(selectionKey)), [currentSelections]);
  const catalogueSelection = useMemo(() => {
    if (!managingCatalogue) return null;
    return (currentSelections || []).find((selection) => selectionKey(selection) === `catalogue:promoworld:${managingCatalogue.key}`) || null;
  }, [currentSelections, managingCatalogue]);
  const excludedIds = useMemo(() => new Set((catalogueSelection?.excludedProductIds || []).map(Number)), [catalogueSelection]);
  const visibleCatalogueProducts = catalogueProducts;

  useEffect(() => {
    if (!open) return;
    setSelectedProductIds([]);
    if (cataloguesOnly) setTab(1);
    setError('');
    setManagingCatalogue(null);
    setCatalogueProducts([]);
    setCatalogueQuery('');
    setCataloguePage(1);
    setCatalogueTotalPages(1);
    setCatalogueTotal(0);
  }, [cataloguesOnly, open]);

  useEffect(() => {
    if (!open || managingCatalogue) return undefined;
    const timer = setTimeout(async () => {
      setLoading(true);
      setError('');
      try {
        if (tab === 0) {
          const response = await axios.get(`${server}/catalog/products`, {
            ...(headers || {}),
            params: { q: query, page: 1, rpp: 36 },
          });
          setProducts(Array.isArray(response?.data?.Results) ? response.data.Results : []);
        } else {
          const response = await axios.get(`${server}/promoshowcase/instances/${instanceUniqueId}/products/catalogues`, {
            ...(headers || {}),
            params: { q: query },
          });
          setCatalogues(Array.isArray(response?.data?.catalogues) ? response.data.catalogues : []);
        }
      } catch (requestError) {
        setError(requestError?.response?.data?.message || 'Could not load PromoWorld right now.');
      } finally {
        setLoading(false);
      }
    }, 250);
    return () => clearTimeout(timer);
  }, [headers, instanceUniqueId, managingCatalogue, open, query, server, tab]);

  useEffect(() => {
    if (!open || !managingCatalogue) return undefined;
    let cancelled = false;
    const timer = setTimeout(async () => {
      setCatalogueLoading(true);
      setError('');
      try {
        const response = await axios.get(
          `${server}/promoshowcase/instances/${instanceUniqueId}/products/catalogues/${encodeURIComponent(managingCatalogue.key)}`,
          {
            ...(headers || {}),
            params: {
              supplierId: managingCatalogue.supplierId || undefined,
              supplierName: managingCatalogue.name || undefined,
              q: catalogueQuery || '',
              page: cataloguePage,
              rpp: 48,
            },
          }
        );
        if (cancelled) return;
        setCatalogueProducts(Array.isArray(response?.data?.products) ? response.data.products : []);
        setCatalogueTotal(Number(response?.data?.total) || 0);
        setCatalogueTotalPages(Math.max(Number(response?.data?.totalPages) || 1, 1));
      } catch (requestError) {
        if (!cancelled) setError(requestError?.response?.data?.message || 'Could not load this catalogue right now.');
      } finally {
        if (!cancelled) setCatalogueLoading(false);
      }
    }, 250);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [cataloguePage, catalogueQuery, headers, instanceUniqueId, managingCatalogue, open, server]);

  const saveSelections = async (nextSelections) => {
    setSaving(true);
    setError('');
    try {
      const response = await axios.put(
        `${server}/promoshowcase/instances/${instanceUniqueId}/products/selections`,
        { selections: nextSelections },
        headers
      );
      await onSaved?.(response?.data);
      return true;
    } catch (requestError) {
      setError(requestError?.response?.data?.message || 'Could not update website products.');
      return false;
    } finally {
      setSaving(false);
    }
  };

  const addSelectedProducts = async () => {
    const additions = selectedProductIds
      .filter((id) => !existingKeys.has(`product:promoworld:${id}`))
      .map((productId) => ({ type: 'product', source: 'promoworld', productId }));
    if (!additions.length) return;
    const ok = await saveSelections([...(currentSelections || []), ...additions]);
    if (ok) setSelectedProductIds([]);
  };

  const addCatalogue = async (catalogue) => {
    const key = `catalogue:promoworld:${catalogue.key}`;
    if (existingKeys.has(key)) return;
    await saveSelections([
      ...(currentSelections || []),
      {
        type: 'catalogue',
        source: 'promoworld',
        catalogueKey: catalogue.key,
        supplierId: catalogue.supplierId || null,
        supplierName: catalogue.name,
        mode: 'live',
        excludedProductIds: [],
      },
    ]);
  };

  const removeCatalogue = async (catalogue) => {
    await saveSelections((currentSelections || []).filter((selection) => selectionKey(selection) !== `catalogue:promoworld:${catalogue.key}`));
    setManagingCatalogue(null);
  };

  const toggleCatalogueProduct = async (productId) => {
    if (!catalogueSelection) return;
    const id = Number(productId);
    const nextExcluded = new Set((catalogueSelection.excludedProductIds || []).map(Number));
    if (nextExcluded.has(id)) nextExcluded.delete(id);
    else nextExcluded.add(id);
    const nextSelections = (currentSelections || []).map((selection) => (
      selectionKey(selection) === selectionKey(catalogueSelection)
        ? { ...selection, excludedProductIds: Array.from(nextExcluded) }
        : selection
    ));
    await saveSelections(nextSelections);
  };

  const removeIndividualProduct = async (productId) => {
    await saveSelections((currentSelections || []).filter((selection) => selectionKey(selection) !== `product:promoworld:${productId}`));
  };

  if (managingCatalogue) {
    return (
      <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="lg" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton size="small" onClick={() => setManagingCatalogue(null)} disabled={saving}><ArrowBackRoundedIcon /></IconButton>
          Manage {managingCatalogue.name}
        </DialogTitle>
        <DialogContent dividers sx={{ minHeight: 560 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, flexWrap: 'wrap', mb: 2 }}>
            <Box>
              <Typography sx={{ fontWeight: 900 }}>{managingCatalogue.name}</Typography>
              <Typography variant="body2" color="text.secondary">
                Products are included automatically. Hide only the products you do not want on this website.
              </Typography>
            </Box>
            <Chip icon={<VisibilityOffRoundedIcon />} label={`${excludedIds.size} hidden`} variant={excludedIds.size ? 'filled' : 'outlined'} />
          </Box>
          <TextField
            size="small"
            fullWidth
            value={catalogueQuery}
            onChange={(event) => {
              setCatalogueQuery(event.target.value);
              setCataloguePage(1);
            }}
            placeholder="Search this catalogue..."
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchRoundedIcon fontSize="small" /></InputAdornment> }}
            sx={{ mb: 2 }}
          />
          {error ? <Typography color="error" variant="body2" sx={{ mb: 2 }}>{error}</Typography> : null}
          {catalogueLoading ? <Box sx={{ display: 'grid', placeItems: 'center', py: 8 }}><CircularProgress size={28} /></Box> : (
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2,minmax(0,1fr))', md: 'repeat(3,minmax(0,1fr))' }, gap: 1.5 }}>
              {visibleCatalogueProducts.map((product) => {
                const id = Number(product?.id);
                const hidden = excludedIds.has(id);
                return (
                  <Box key={id} sx={{ border: '1px solid #e5e7eb', borderRadius: 2, overflow: 'hidden', background: hidden ? '#f8fafc' : '#fff', opacity: hidden ? .72 : 1 }}>
                    <Box sx={{ height: 130, background: '#f8fafc', display: 'grid', placeItems: 'center' }}>
                      {productImage(product) ? <img src={productImage(product)} alt="" style={{ width: '100%', height: '100%', objectFit: 'contain' }} /> : <Inventory2RoundedIcon sx={{ color: '#94a3b8' }} />}
                    </Box>
                    <Box sx={{ p: 1.25, display: 'grid', gap: .5 }}>
                      <Typography sx={{ fontWeight: 800, lineHeight: 1.2 }}>{productName(product)}</Typography>
                      <Typography variant="caption" color="text.secondary">#{productNumber(product)}</Typography>
                      <Button size="small" variant={hidden ? 'outlined' : 'text'} disabled={saving} onClick={() => toggleCatalogueProduct(id)}>
                        {hidden ? 'Show on website' : 'Hide from website'}
                      </Button>
                    </Box>
                  </Box>
                );
              })}
            </Box>
          )}
          <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Typography variant="caption" color="text.secondary">
              {catalogueTotal.toLocaleString()} product{catalogueTotal === 1 ? '' : 's'} · page {cataloguePage} of {catalogueTotalPages}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button size="small" variant="outlined" disabled={catalogueLoading || cataloguePage <= 1} onClick={() => setCataloguePage((page) => Math.max(1, page - 1))}>Previous</Button>
              <Button size="small" variant="outlined" disabled={catalogueLoading || cataloguePage >= catalogueTotalPages} onClick={() => setCataloguePage((page) => Math.min(catalogueTotalPages, page + 1))}>Next</Button>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'space-between' }}>
          <Button color="error" onClick={() => removeCatalogue(managingCatalogue)} disabled={saving}>Remove catalogue</Button>
          <Button onClick={onClose} disabled={saving}>Done</Button>
        </DialogActions>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="lg" fullWidth>
      <DialogTitle>{cataloguesOnly ? 'Website catalogues' : 'Add products to this website'}</DialogTitle>
      <DialogContent dividers sx={{ minHeight: 560 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {cataloguesOnly
            ? 'Add complete PromoWorld supplier catalogues. Use the standard Add Product cart action for individual products.'
            : 'Choose individual PromoWorld products or add a complete supplier catalogue. Added catalogues stay connected so newly available products can be included on the next website refresh.'}
        </Typography>

        {!cataloguesOnly ? (
          <Tabs value={tab} onChange={(_event, value) => setTab(value)} sx={{ mb: 2 }}>
            <Tab icon={<Inventory2RoundedIcon fontSize="small" />} iconPosition="start" label="Products" />
            <Tab icon={<CollectionsBookmarkRoundedIcon fontSize="small" />} iconPosition="start" label="Catalogues" />
          </Tabs>
        ) : null}

        <TextField
          size="small"
          fullWidth
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder={cataloguesOnly || tab === 1 ? 'Search catalogues...' : 'Search products, SKU, supplier...'}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchRoundedIcon fontSize="small" /></InputAdornment> }}
          sx={{ mb: 2 }}
        />

        {error ? <Typography color="error" variant="body2" sx={{ mb: 2 }}>{error}</Typography> : null}
        {loading ? <Box sx={{ display: 'grid', placeItems: 'center', py: 8 }}><CircularProgress size={28} /></Box> : null}

        {!loading && tab === 0 ? (
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2,minmax(0,1fr))', md: 'repeat(3,minmax(0,1fr))' }, gap: 1.5 }}>
            {products.map((product) => {
              const id = Number(product?.id);
              const alreadyAdded = existingKeys.has(`product:promoworld:${id}`);
              const selected = selectedProductIds.includes(id);
              return (
                <Box key={id} sx={{ border: '1px solid #e5e7eb', borderRadius: 2, overflow: 'hidden', background: '#fff' }}>
                  <Box sx={{ height: 150, background: '#f8fafc', display: 'grid', placeItems: 'center' }}>
                    {productImage(product) ? <img src={productImage(product)} alt="" style={{ width: '100%', height: '100%', objectFit: 'contain' }} /> : <Inventory2RoundedIcon sx={{ color: '#94a3b8' }} />}
                  </Box>
                  <Box sx={{ p: 1.5, display: 'grid', gap: .6 }}>
                    <Typography sx={{ fontWeight: 800, lineHeight: 1.2 }}>{productName(product)}</Typography>
                    <Typography variant="caption" color="text.secondary">{productSupplier(product)} · #{productNumber(product)}</Typography>
                    <Divider />
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      {alreadyAdded ? (
                        <Box sx={{ display: 'flex', gap: .75, alignItems: 'center' }}>
                          <Chip size="small" label="Added" color="success" />
                          <Button size="small" color="error" disabled={saving} onClick={() => removeIndividualProduct(id)}>Remove</Button>
                        </Box>
                      ) : (
                        <Button size="small" onClick={() => setSelectedProductIds((prev) => selected ? prev.filter((value) => value !== id) : [...prev, id])}>
                          <Checkbox size="small" checked={selected} sx={{ p: 0, mr: .5 }} /> {selected ? 'Selected' : 'Select'}
                        </Button>
                      )}
                    </Box>
                  </Box>
                </Box>
              );
            })}
          </Box>
        ) : null}

        {!loading && tab === 1 ? (
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2,minmax(0,1fr))' }, gap: 1.5 }}>
            {catalogues.map((catalogue) => {
              const alreadyAdded = existingKeys.has(`catalogue:promoworld:${catalogue.key}`);
              const current = (currentSelections || []).find((selection) => selectionKey(selection) === `catalogue:promoworld:${catalogue.key}`);
              const hiddenCount = current?.excludedProductIds?.length || 0;
              return (
                <Box key={catalogue.key} sx={{ border: '1px solid #e5e7eb', borderRadius: 2, p: 2, display: 'grid', gap: 1 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
                    <Box>
                      <Typography sx={{ fontWeight: 900 }}>{catalogue.name}</Typography>
                      <Typography variant="body2" color="text.secondary">{catalogue.productCount} product{catalogue.productCount === 1 ? '' : 's'} available{hiddenCount ? ` · ${hiddenCount} hidden` : ''}</Typography>
                    </Box>
                    <Chip size="small" label="PromoWorld" variant="outlined" />
                  </Box>
                  <Typography variant="caption" color="text.secondary">Keeps this website connected to the catalogue as products change.</Typography>
                  {alreadyAdded ? (
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      <Button variant="contained" disabled={saving} onClick={() => setManagingCatalogue(catalogue)}>Manage products</Button>
                      <Button color="error" variant="outlined" disabled={saving} onClick={() => removeCatalogue(catalogue)}>Remove catalogue</Button>
                    </Box>
                  ) : (
                    <Button variant="contained" disabled={saving} onClick={() => addCatalogue(catalogue)}>Add catalogue</Button>
                  )}
                </Box>
              );
            })}
          </Box>
        ) : null}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Close</Button>
        {tab === 0 ? (
          <Button variant="contained" disabled={!selectedProductIds.length || saving} onClick={addSelectedProducts}>
            {saving ? 'Adding...' : `Add selected${selectedProductIds.length ? ` (${selectedProductIds.length})` : ''}`}
          </Button>
        ) : null}
      </DialogActions>
    </Dialog>
  );
}
